import { useState, useEffect, useCallback, useRef } from "react";
import { Modal } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import {
  PlusOutlined,
  MinusOutlined,
  ScissorOutlined,
  CheckOutlined,
  BarsOutlined,
  PieChartOutlined,
} from "@ant-design/icons";
import { useMenuItems, useMenuModifiers } from "../../../../../hooks/useMenu";
import { useSplitBill } from "../../../../../hooks/useBills";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useAppSelector } from "../../../../../store/hooks";
import { NewTableModal } from "../manage/NewTableModal";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { SplitAndPayModal } from "../payment/SplitAndPayModal";
import type { BillOut } from "../../../../../api/bills";
import { attachedDepositOf } from "../deposits/billDeposit";
import { requestedBillDiscount } from "../discounts/billAmountDiscount";
import { useViewport } from "../../../../../hooks/useViewport";
import { heldElementCountAmong } from "../../../../../lib/kitchenHold";
import { useHeldOrdersConfirm } from "../kitchenHold/useHeldOrdersConfirm";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
  SoftPrimaryButton,
} from "../../modalPrimitives";

interface SplitBillModalProps {
  open: boolean;
  onClose: () => void;
  billId: number;
  bill: BillOut | undefined;
}

interface SelectedQuantities {
  [billElementItemId: number]: number;
}

export const SplitBillModal = ({
  open,
  onClose,
  billId,
  bill,
}: SplitBillModalProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const allItems = useMenuItems();
  const allModifiers = useMenuModifiers();
  const splitBillMutation = useSplitBill();
  const message = useOrderingMessage();
  const permissions = useAppSelector((state) => state.auth.permissions);
  const canPaymentCash = permissions.some((p) => p.name === "payment_cash");
  const canPaymentCard = permissions.some((p) => p.name === "payment_card");
  const canCloseBill1 = permissions.some((p) => p.name === "close_bill_1");
  // Закачен депозит („Използвай депозит") се приспада от ЦЯЛАТА сметка при
  // „Закрий" — „Плати избраните" би платила част без него, затова е заключено.
  const hasAttachedDeposit = !!attachedDepositOf(bill);
  // Отстъпката в € на цялата сметка остава на тази сметка — „Плати
  // избраните" би взела избраните по пълни цени, затова е заключено.
  const hasBillDiscount = requestedBillDiscount(bill) > 0;
  const [selectedQuantities, setSelectedQuantities] =
    useState<SelectedQuantities>({});
  // „По продукти" = ръчен избор; „По равно" = половината от всеки ред е
  // предварително избрана (сметката се дели на две части).
  const [splitMode, setSplitMode] = useState<"products" | "equal">("products");
  const [showTableNameModal, setShowTableNameModal] = useState(false);
  const [showSplitAndPayModal, setShowSplitAndPayModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [pendingTableName, setPendingTableName] = useState("");
  // Името на заета маса на ДРУГ сервитьор при липсващо право
  // split_bill_to_operator — показва информативен модал без опция за сливане.
  const [occupiedByOtherName, setOccupiedByOtherName] = useState<string | null>(
    null,
  );
  // Разделяне в ход: прозорецът за номера се затваря веднага, а изборът
  // остава — без ключалката второ „Раздели" (клик/Enter) би преместило същите
  // бройки още веднъж (BE приема, докато в сметката има количество).
  const splitInFlightRef = useRef(false);
  const isSplitting = splitBillMutation.isPending;
  // „Плати избраните" закрива новата сметка — задържаните поръчки сред
  // избраните редове влизат в кухнята; питането е преди плащането.
  // „Към друга маса" ги мести задържани (BE), без питане.
  const { confirmHeldOrders, heldOrdersModal, heldOrdersConfirmOpen } =
    useHeldOrdersConfirm();

  const getTranslation = (
    translations: Array<{ languageCode: string; name: string }>,
    code = i18n.language,
  ) => translations.find((tr) => tr.languageCode === code)?.name || "";

  const billItems =
    bill?.elements.flatMap((el) =>
      el.items.map((item) => ({
        ...item,
        billElementId: el.id,
        comment: el.comment,
      })),
    ) || [];

  const handleQuantityChange = (itemId: number, delta: number) => {
    const item = billItems.find((i) => i.id === itemId);
    if (!item) return;
    const maxQty = parseFloat(item.quantity);
    const currentQty = selectedQuantities[itemId] || 0;
    const newQty = Math.max(0, Math.min(maxQty, currentQty + delta));
    setSelectedQuantities((prev) => ({ ...prev, [itemId]: newQty }));
  };

  // Клик по реда добавя по една бройка (до наличното); ред с една бройка (или
  // по-малко) се избира/маха цял. „+/−" ги има само при избран ред с повече бройки.
  const handleRowClick = (itemId: number) => {
    const item = billItems.find((i) => i.id === itemId);
    if (!item) return;
    const maxQty = parseFloat(item.quantity);
    const currentQty = selectedQuantities[itemId] || 0;
    if (maxQty <= 1) {
      setSelectedQuantities((prev) => ({ ...prev, [itemId]: currentQty > 0 ? 0 : maxQty }));
      return;
    }
    if (currentQty < maxQty) handleQuantityChange(itemId, 1);
  };

  // Отметката: избран ред се маха изцяло, неизбран — с една бройка.
  const handleCheckboxClick = (itemId: number) => {
    const item = billItems.find((i) => i.id === itemId);
    if (!item) return;
    const maxQty = parseFloat(item.quantity);
    const currentQty = selectedQuantities[itemId] || 0;
    setSelectedQuantities((prev) => ({
      ...prev,
      [itemId]: currentQty > 0 ? 0 : Math.min(1, maxQty),
    }));
  };

  const handleModeChange = (mode: "products" | "equal") => {
    setSplitMode(mode);
    if (mode === "equal") {
      setSelectedQuantities(
        Object.fromEntries(
          billItems.map((item) => [item.id, parseFloat(item.quantity) / 2]),
        ),
      );
    } else {
      setSelectedQuantities({});
    }
  };

  const selectedItems = billItems.filter(
    (item) => (selectedQuantities[item.id] || 0) > 0,
  );

  // „Избери всичко" — изведено от селекцията (както при сторно): маха ли се
  // количество от ред, чекбоксът сам угасва. Повторно натискане чисти всичко.
  const allSelected =
    billItems.length > 0 &&
    billItems.every(
      (item) => (selectedQuantities[item.id] || 0) >= parseFloat(item.quantity),
    );

  const handleToggleSelectAll = () => {
    if (allSelected) {
      setSelectedQuantities({});
    } else {
      setSelectedQuantities(
        Object.fromEntries(
          billItems.map((item) => [item.id, parseFloat(item.quantity)]),
        ),
      );
    }
  };

  // Цена на ред (продукт + добавки) — добавките не са в item.total_sum.
  // Крайните им суми се четат от самите тях: отстъпката върху продукт ги
  // обхваща (BE ги преизчислява), промяната на цената — не.
  const getLineTotal = (item: (typeof billItems)[number]) => {
    const additionsTotal = item.additions.reduce(
      (sum, a) => sum + parseFloat(a.total_sum),
      0,
    );
    return parseFloat(item.total_sum) + additionsTotal;
  };

  const totalSelectedAmount = selectedItems.reduce((sum, item) => {
    const qty = selectedQuantities[item.id] || 0;
    const maxQty = parseFloat(item.quantity);
    return sum + (getLineTotal(item) / maxQty) * qty;
  }, 0);

  // Новата маса остава на същия сервитьор — изборът на сервитьор при
  // разделяне е махнат (правото `split_bill_to_operator` не се пипа).
  const submitSplit = async (newTableName: string, forceExisting = false) => {
    if (splitInFlightRef.current) return;
    splitInFlightRef.current = true;
    const elements = selectedItems.map((item) => ({
      billElementId: item.id,
      quantity: selectedQuantities[item.id],
    }));
    try {
      await splitBillMutation.mutateAsync({
        billId,
        data: {
          splitTableName: newTableName,
          comment: null,
          elements,
          ...(forceExisting && { splitInExistingBill: true }),
        },
      });
      setSelectedQuantities({});
      setSplitMode("products");
      setShowTableNameModal(false);
      setShowConfirmModal(false);
      setPendingTableName("");
      onClose();
    } catch (error: unknown) {
      console.error("Split bill error:", error);
      const detail =
        (error as { response?: { data?: { detail?: string } } })?.response
          ?.data?.detail;
      const errorMessage = getApiErrorMessage(error, t("splitBill.splitError"));
      // Маса на ДРУГ сервитьор без право split_bill_to_operator: сливането е
      // забранено от BE — информираме, без въпрос за прехвърляне.
      if (
        typeof detail === "string" &&
        detail.includes("заета от друг сервитьор")
      ) {
        setShowTableNameModal(false);
        setPendingTableName("");
        setOccupiedByOtherName(newTableName);
      } else if (
        !forceExisting &&
        typeof detail === "string" &&
        detail.includes("вече съществува и е отворена")
      ) {
        setPendingTableName(newTableName);
        setConfirmMessage(errorMessage);
        setShowTableNameModal(false);
        setShowConfirmModal(true);
      } else {
        message.error(errorMessage);
      }
    } finally {
      splitInFlightRef.current = false;
    }
  };

  // След номера на масата — направо разделяне (без избор на сервитьор).
  const handleTableNameSubmit = (newTableName: string) => {
    setPendingTableName(newTableName);
    setShowTableNameModal(false);
    submitSplit(newTableName, false);
  };

  const handleConfirmTransfer = () => submitSplit(pendingTableName, true);
  const handleCancelTransfer = () => {
    setShowConfirmModal(false);
    setPendingTableName("");
    setConfirmMessage("");
  };

  const handleClose = useCallback(() => {
    setSelectedQuantities({});
    setSplitMode("products");
    setShowTableNameModal(false);
    setShowSplitAndPayModal(false);
    setShowConfirmModal(false);
    setPendingTableName("");
    setConfirmMessage("");
    setOccupiedByOtherName(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        showTableNameModal ||
        showSplitAndPayModal ||
        showConfirmModal ||
        heldOrdersConfirmOpen ||
        occupiedByOtherName !== null
      )
        return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (selectedItems.length > 0 && !isSplitting) setShowTableNameModal(true);
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    open,
    selectedItems,
    showTableNameModal,
    showSplitAndPayModal,
    showConfirmModal,
    heldOrdersConfirmOpen,
    occupiedByOtherName,
    handleClose,
    isDesktop,
    isSplitting,
  ]);

  const handleSplitAndPayClick = async () => {
    const heldCount = heldElementCountAmong(
      bill,
      selectedItems.map((item) => item.id),
    );
    if (heldCount > 0 && !(await confirmHeldOrders(heldCount))) return;
    setShowSplitAndPayModal(true);
  };

  const width = isMobile ? undefined : isTablet ? 520 : 640;
  const bodyPadClass = isNarrow ? "px-4" : "px-6";
  const billTotal = parseFloat(bill?.total_amount ?? "0");

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={width}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <div className="flex max-h-[85vh] flex-col bg-pos-card text-pos-ink">
          <ModalHeader
            icon={<ScissorOutlined />}
            title={t("splitBill.title")}
            subtitle={t("splitBill.subtitle", {
              name: bill?.name ?? "",
              total: billTotal.toFixed(2),
            })}
            onClose={handleClose}
            isNarrow={isNarrow}
          />

          {/* Режим: по продукти (ръчен избор) / по равно (половината от всеки ред) */}
          <div
            className={`${bodyPadClass} flex gap-2.5 pt-3.5 pb-2.5 ${
              isNarrow
                ? "flex-col items-center"
                : "items-center justify-between"
            }`}
          >
            {/* Същият сегментиран превключвател като „Плочки | Таблица" в OrderFooter */}
            <span className="inline-flex h-10 shrink-0 items-stretch overflow-hidden rounded-[11px] border border-pos-ctl bg-pos-card">
              <SegmentButton
                icon={<BarsOutlined className="text-[15px]" />}
                label={t("splitBill.modeByProducts")}
                active={splitMode === "products"}
                onClick={() => handleModeChange("products")}
              />
              <SegmentButton
                icon={<PieChartOutlined className="text-[15px]" />}
                label={t("splitBill.modeEqual")}
                active={splitMode === "equal"}
                onClick={() => handleModeChange("equal")}
                divider
              />
            </span>
            {/* Същият стил като подзаглавието в хедъра */}
            <span className="truncate text-[13px] font-medium leading-normal text-pos-muted">
              {t("splitBill.selectedProducts", {
                count: selectedItems.length,
              })}
            </span>
          </div>

          <div
            className={`${bodyPadClass} flex min-h-0 flex-col gap-2 overflow-y-auto pb-4`}
          >
            {billItems.map((item) => {
              const itemName =
                item.item_names?.[i18n.language] ||
                item.item_names?.["bg"] ||
                Object.values(item.item_names || {})[0] ||
                t("common.product");
              const maxQty = parseFloat(item.quantity);
              const selectedQty = selectedQuantities[item.id] || 0;
              const unitPrice = getLineTotal(item) / parseFloat(item.quantity);
              const active = selectedQty > 0;

              const hasStepper = active && maxQty > 1;

              return (
                // Целият ред е за докосване: добавя бройка (ред с 1 бройка —
                // избира/маха); отметката и „+/−" не стигат до реда.
                <div
                  key={item.id}
                  role="button"
                  tabIndex={-1}
                  aria-pressed={active}
                  onClick={() => handleRowClick(item.id)}
                  className={`cursor-pointer select-none rounded-[10px] border px-3 py-2.5 transition-colors ${
                    active
                      ? "border-pos-accent/30 bg-pos-marked"
                      : "border-pos-line3 bg-pos-panel hover:border-pos-ctl"
                  }`}
                >
                  <div className="flex min-h-[30px] items-center justify-between gap-2.5">
                    <div className="flex min-w-0 flex-1 items-center gap-2.5">
                      <RowCheckbox
                        checked={active}
                        label={itemName}
                        onToggle={() => handleCheckboxClick(item.id)}
                      />
                      <div className="flex min-w-0 flex-1 items-baseline gap-2">
                        <span className="min-w-0 truncate text-[13.5px] font-semibold text-pos-ink">
                          {itemName}
                        </span>
                        <span className="shrink-0 font-pos-mono text-[11.5px] text-pos-muted">
                          {unitPrice.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {hasStepper ? (
                      <div
                        className="flex shrink-0 items-center gap-1"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <QtyBtn
                          icon={<MinusOutlined />}
                          onClick={() => handleQuantityChange(item.id, -1)}
                          disabled={selectedQty === 0}
                        />
                        {/* Избраното количество, а под него „от N" — като в „Сторно" */}
                        <div className="flex min-w-[48px] flex-col items-center leading-none">
                          <span className="font-pos-mono text-[15px] font-bold tabular-nums text-pos-accent-ink">
                            {selectedQty}
                          </span>
                          <span className="mt-0.5 font-pos-mono text-[10px] text-pos-muted">
                            {t("cancelItem.ofQuantity", { max: maxQty })}
                          </span>
                        </div>
                        <QtyBtn
                          icon={<PlusOutlined />}
                          onClick={() => handleQuantityChange(item.id, 1)}
                          disabled={selectedQty >= maxQty}
                        />
                      </div>
                    ) : (
                      // Без „+/−": колко бройки има (избран ред с 1 бройка — колко е избрано).
                      <span
                        className={`shrink-0 font-pos-mono text-[13px] font-semibold tabular-nums ${
                          active ? "text-pos-accent-ink" : "text-pos-muted"
                        }`}
                      >
                        ×{active ? selectedQty : maxQty}
                      </span>
                    )}
                  </div>

                  {(item.modifiers.length > 0 || item.additions.length > 0) && (
                    <div className="mt-1.5 ml-2 flex flex-col gap-0.5 border-l-2 border-pos-line pl-2.5 text-[11px] text-pos-soft">
                      {item.modifiers.length > 0 && (
                        <div>
                          {t("splitBill.modifiersLabel")}{" "}
                          {item.modifiers
                            .map((m) => {
                              const mod = allModifiers.find(
                                (modifier) =>
                                  modifier.id.toString() === m.modifier_id,
                              );
                              return mod
                                ? getTranslation(mod.translations)
                                : "";
                            })
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}
                      {item.additions.length > 0 && (
                        <div>
                          {t("splitBill.addonsLabel")}{" "}
                          {item.additions
                            .map((a) => {
                              const addonItem = allItems.find(
                                (i) => i.id === a.addition_item_id,
                              );
                              return addonItem
                                ? `${getTranslation(addonItem.itemLangs)} x${a.quantity}`
                                : "";
                            })
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Обобщение: „Избери всичко" (бутон) вляво, брой избрани + сума вдясно */}
          <div
            className={`flex items-center justify-between gap-2.5 border-t border-pos-line2 ${
              isNarrow ? "px-4 py-2.5" : "px-6 py-3"
            }`}
          >
            <SelectAllButton
              checked={allSelected}
              onToggle={handleToggleSelectAll}
              label={t("splitBill.selectAll")}
            />
            <div className="flex min-w-0 items-baseline gap-2">
              <span className="text-[13px] font-semibold text-pos-soft2">
                {t("splitBill.sumLabel")}
              </span>
              <span className="font-pos-mono text-lg font-bold tabular-nums text-pos-accent-ink">
                {totalSelectedAmount.toFixed(2)}
              </span>
            </div>
          </div>

          {(hasAttachedDeposit || hasBillDiscount) && (
            <div
              className={`border-t border-pos-line2 text-[12.5px] font-semibold text-pos-soft2 ${
                isNarrow ? "px-4 py-2" : "px-6 py-2.5"
              }`}
            >
              {t(hasAttachedDeposit ? "splitBill.depositBlocksPay" : "splitBill.billDiscountBlocksPay")}
            </div>
          )}

          <ModalFooter isNarrow={isNarrow} columns="1fr 2fr 2fr">
            <GhostButton
              label={t("splitBill.cancelButton")}
              onClick={handleClose}
              shortcut={isDesktop ? "Esc" : undefined}
            />
            <PrimaryButton
              label={t("splitBill.splitButton")}
              onClick={() => setShowTableNameModal(true)}
              disabled={selectedItems.length === 0 || isSplitting}
              shortcut={isDesktop ? "⏎" : undefined}
            />
            <SoftPrimaryButton
              label={t("splitBill.splitAndPayButton")}
              onClick={() => void handleSplitAndPayClick()}
              disabled={
                selectedItems.length === 0 || isSplitting || hasAttachedDeposit || hasBillDiscount
              }
            />
          </ModalFooter>
        </div>
      </Modal>

      <NewTableModal
        open={showTableNameModal}
        onClose={() => setShowTableNameModal(false)}
        onSubmit={handleTableNameSubmit}
        allowExistingName
        moveFrom={bill?.name ?? ""}
      />

      <ConfirmActionModal
        open={showConfirmModal}
        title={t("confirmSplit.title")}
        message={confirmMessage}
        question={t("confirmSplit.transferQuestion")}
        confirmLabel={t("confirmSplit.transferButton")}
        onCancel={handleCancelTransfer}
        onConfirm={handleConfirmTransfer}
      />

      {/* Заета маса на друг сервитьор без право split_bill_to_operator —
          само информация, няма опция за сливане (BE-то го забранява). */}
      <ConfirmActionModal
        open={occupiedByOtherName !== null}
        title={t("confirmSplit.title")}
        message={t("confirmSplit.occupiedByOtherWaiter", {
          name: occupiedByOtherName ?? "",
        })}
        question={t("confirmSplit.noTransferPermission")}
        cancelLabel={t("confirmSplit.understoodButton")}
        onCancel={() => setOccupiedByOtherName(null)}
      />

      {heldOrdersModal}

      <SplitAndPayModal
        open={showSplitAndPayModal}
        onClose={() => setShowSplitAndPayModal(false)}
        onSuccess={() => {
          setShowSplitAndPayModal(false);
          setSelectedQuantities({});
          setSplitMode("products");
          onClose();
        }}
        billId={billId}
        sourceTableName={bill?.name ?? ""}
        elements={selectedItems.map((item) => ({
          billElementId: item.id,
          quantity: selectedQuantities[item.id],
        }))}
        totalAmount={totalSelectedAmount}
        canPaymentCash={canPaymentCash}
        canPaymentCard={canPaymentCard}
        canCloseBill1={canCloseBill1}
        customerId={bill?.customer_id ?? null}
      />
    </>
  );
};

// „Избери всичко" — бутон с чекбокс-квадратче вътре (по-лесен за натискане);
// самото квадратче светва в accent при активно, бутонът остава неутрален.
const SelectAllButton = ({
  checked,
  onToggle,
  label,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
}) => (
  <button
    type="button"
    onClick={onToggle}
    className="inline-flex h-9 shrink-0 cursor-pointer items-center gap-2 rounded-[10px] border border-pos-ctl bg-pos-card px-3 text-[13px] font-semibold text-pos-ink2 transition-colors hover:bg-pos-canvas"
  >
    {/* Квадратче като чекбокс — светва в accent при активно */}
    <span
      className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border text-white transition-colors ${
        checked ? "border-pos-accent bg-pos-accent" : "border-pos-check bg-pos-canvas"
      }`}
    >
      {checked && <CheckOutlined className="text-[10px]" />}
    </span>
    <span>{label}</span>
  </button>
);

// Отметката на реда — квадратчето на „Избери всичко"; кликът не стига до
// реда (той добавя бройка).
const RowCheckbox = ({
  checked,
  label,
  onToggle,
}: {
  checked: boolean;
  label: string;
  onToggle: () => void;
}) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    aria-label={label}
    onClick={(event) => {
      event.stopPropagation();
      onToggle();
    }}
    className="-m-1.5 inline-flex shrink-0 cursor-pointer items-center justify-center p-1.5"
  >
    <span
      className={`inline-flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border text-white transition-colors ${
        checked ? "border-pos-accent bg-pos-accent" : "border-pos-check bg-pos-canvas"
      }`}
    >
      {checked && <CheckOutlined className="text-[10px]" />}
    </span>
  </button>
);

// Сегмент от превключвателя на режима („По продукти" / „По равно") — класовете
// са тези на `segment()` в menu/OrderFooter.tsx (изглед плочки/таблица).
const SegmentButton = ({
  icon,
  label,
  active,
  onClick,
  divider,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  divider?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex cursor-pointer items-center gap-1.5 px-3.5 text-[13px] font-semibold transition-colors ${
      divider ? "border-l border-pos-ctl" : ""
    } ${
      active
        ? "bg-pos-marked text-pos-accent-ink"
        : "text-pos-ink2 hover:bg-pos-panel"
    }`}
  >
    {icon}
    {label}
  </button>
);

const QtyBtn = ({
  icon,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  // Tailwind hover: (v4) важи само на устройства с истински hover — на телефон
  // докосването НЕ оцветява бутона (JS onMouseEnter „залепваше" фона след tap)
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-[30px] h-[30px] rounded-lg bg-pos-card border border-pos-ctl text-pos-soft2 inline-flex items-center justify-center text-xs cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:bg-pos-marked enabled:hover:border-pos-accent/30 enabled:hover:text-pos-accent-ink"
  >
    {icon}
  </button>
);
